import { route$ } from "./utils";

// The Intercom help centre, behind our Cloudflare zone. `/contact` is a Cloudflare
// redirect to the support form: link to it, never to the form itself
// (docs/DECISIONS.md, 2026-10-14).
const KNOWLEDGE_BASE = "https://support.shuttlerock.com";

/**
 * Make sure to mirror this route with cloud-ui/src/routes as
 * typing in the route$ function is deduced from the cloud-ui/src/routes structure
 * */
export const Routes = {
  Home: route$("/"),
  Admin: {
    BrandsRedirect: route$("/brands"),
    BrandProfileRedirect: route$("/brandProfile"),
    Clients: route$("/client"),
    ClientUsers: route$("/client/:integrationKey/users"),
    ClientBrands: route$("/client/:integrationKey/brands"),
    EditClient: route$("/client/:integrationKey"),
    InviteClientUser: route$("/client/:integrationKey/invite"),
    NewClient: route$("/client/new"),
    Users: route$("/users"),
    AccountInsights: route$("/accountInsights"),
  },
  Auth: {
    AcceptInvite: route$("/invitations/:invitationId"),
    Profile: route$("/profile"),
    SignIn: route$("/signin"),
    Callback: route$("/callback"),
    SignOut: route$("/signout"),
  },
  Concept: {
    Base: route$("/concepts/:conceptKey"),
    Review: route$("/concepts/:conceptKey/review"),
  },
  Creator: {
    Brief: route$("/creator/briefs/:ugcOrderKey"),
    Profile: "/creator/profile/:creatorKey",
  },
  Customer: {
    BrandProfile: route$("/brands/:integrationKey/profile"),
  },
  Delivery: {
    DeliveryBase: route$("/projects/:projectKey/delivery"),
    get AllCreatives() {
      return route$(`${this.DeliveryBase}/creatives` as const);
    },
    get Concepts() {
      return route$(`${this.DeliveryBase}/concepts` as const);
    },
    get ConceptCreatives() {
      return route$(`${this.DeliveryBase}/concepts/:conceptKey/creatives` as const);
    },
    get ConceptVariations() {
      return route$(`${this.DeliveryBase}/concepts/:conceptKey/variations` as const);
    },
    get VariationCreatives() {
      return route$(
        `${this.DeliveryBase}/concepts/:conceptKey/variations/:variationKey/creatives` as const,
      );
    },
    NewVariation: route$("/projects/:projectKey/new-variation/:conceptKey"),
    EditVariation: route$(
      "/projects/:projectKey/concepts/:conceptKey/edit-variation/:variationKey",
    ),
    EditorSession: route$("/projects/:projectKey/editor-session/:editorSessionKey"),
    EditorSessionConceptCreatives: route$(
      "/projects/:projectKey/concepts/:conceptKey/editor-session/:editorSessionKey",
    ),
    EditorSessionVariationCreatives: route$(
      "/projects/:projectKey/concepts/:conceptKey/variations/:variationKey/editor-session/:editorSessionKey",
    ),
  },
  External: {
    ContactSupport: `${KNOWLEDGE_BASE}/contact`,
    KnowledgeBase: KNOWLEDGE_BASE,
    TurnaroundTime: `${KNOWLEDGE_BASE}/en/articles/9880884-turnaround-times`,
    AiInfoSupport: `${KNOWLEDGE_BASE}/en/articles/9840914-ai-enhanced-creative`,
  },
  Projects: {
    All: route$("/projects/all"),
    New: '/?newProject=%7B"open"%3Atrue%7D',
    Projects: route$("/projects"),
    DraftProjects: route$("/projects/drafts"),
    ActiveProjects: route$("/projects/active"),
    CompletedProjects: route$("/projects/complete"),
    Upload: route$("/projects/:projectKey/upload"),
    Overview: route$("/projects/:projectKey/overview"),
    ProductionBrief: route$("/projects/:projectKey/production"),
    BriefingBase: route$("/projects/:projectKey/brief"),
    ConceptBrief: route$("/projects/:projectKey/brief/concepts/:conceptKey"),
    ConceptDetails: route$("concepts/:conceptKey"),
    UgcConceptDetails: route$("concepts/:conceptKey/ugc"),
  },
  ReactAdmin: "/management",
  Editor: route$("/projects/:projectKey/editor/:conceptKey?/:formatKey?"),
  Revision: {
    Base: route$("/projects/:projectKey/review"),
    Canvas: route$("/projects/:projectKey/review/canvas"),
    Edit: route$("/projects/:projectKey/review/:creativeKey/edit/:generativeEditKey?"),
    CompareVersions: route$("/projects/:projectKey/review/compare"),
    Creative: route$("/projects/:projectKey/review/:creativeKey?"),
    CreativeForVariation: route$("/projects/:projectKey/review/variation/:specificationKey"),
    SelectedRevision: route$(
      "/projects/:projectKey/review/:creativeKey?/revision/:revisionSequenceId",
    ),
    SelectedVariationRevision: route$(
      "/projects/:projectKey/review/variation/:specificationKey/revision/:revisionSequenceId",
    ),
  },
  SalesTools: {
    CreativeLibrary: route$("/creative-library"),
  },
} as const;
